<script setup>
import { useId } from 'vue';
import XlSkeleton from './XlSkeleton.vue';
const props = defineProps({
  label: { type: String, required: true },
  value: { type: [Number, String], default: 0 },
  hint: { type: String, default: '' },
  tone: {
    type: String,
    default: '',
    validator: (v) => ['', 'brand', 'success', 'warning', 'error'].includes(v),
  },
  clickable: { type: Boolean, default: false },
  // Explains the term in the label. Rendered in CSS rather than through
  // XlTooltip: a clickable card is a real <button>, and XlTooltip's trigger
  // takes focus, which must not be nested inside one.
  tooltip: { type: String, default: '' },
  // The number is on its way: a placeholder holds its line, so the card does
  // not grow when it lands and nobody reads a nought that is not one.
  loading: { type: Boolean, default: false },
});

const emit = defineEmits(['select']);

const tipId = `xl-kpi-tip-${useId()}`;

// A card that opens a list is a control, so a clickable one renders as a real
// button — reachable by keyboard and announced as one. The element has to be
// the single root, so this note cannot live in the template: a leading comment
// node there would make the component a fragment and break attribute
// fallthrough along with it.
</script>

<template>
  <component
    :is="props.clickable ? 'button' : 'div'"
    :type="props.clickable ? 'button' : null"
    class="xl-kpi"
    :class="{ 'xl-kpi--clickable': props.clickable }"
    :aria-describedby="props.tooltip && props.clickable ? tipId : undefined"
    @click="props.clickable && emit('select')"
  >
    <span
      class="xl-kpi__label"
      :class="{ 'xl-kpi__label--explained': props.tooltip }"
      :tabindex="props.tooltip && !props.clickable ? 0 : undefined"
      :aria-describedby="props.tooltip && !props.clickable ? tipId : undefined"
    >{{ props.label }}</span>
    <span v-if="props.tooltip" :id="tipId" class="xl-kpi__tip" role="tooltip" aria-hidden="true">{{ props.tooltip }}</span>
    <span class="xl-kpi__value" :class="props.tone ? `xl-kpi__value--${props.tone}` : null">
      <XlSkeleton v-if="props.loading" width="44px" height="0.8em" radius="8px" class="xl-kpi__value-sk" data-testid="kpi-value-loading" />
      <template v-else>{{ props.value }}</template>
    </span>
    <span v-if="props.hint" class="xl-kpi__hint">{{ props.hint }}</span>
  </component>
</template>

<style scoped>
.xl-kpi, .xl-kpi * { box-sizing: border-box; }

.xl-kpi {
  display: block;
  width: 100%;
  padding: 16px 18px;
  border: 1px solid var(--xl-color-border);
  border-radius: var(--xl-radius-md);
  background: #fff;
  box-shadow: var(--xl-shadow-1);
  font-family: var(--xl-font-family);
  text-align: left;
}

.xl-kpi--clickable {
  cursor: pointer;
  transition: border-color 0.14s, box-shadow 0.14s;
}

.xl-kpi { position: relative; }

.xl-kpi__label--explained {
  cursor: help;
  text-decoration: underline dotted;
  text-underline-offset: 3px;
  text-decoration-color: var(--xl-color-outline);
  text-decoration-thickness: 1px;
}

/* A hidden tip must be display:none: an invisible one still widens the page's scroll area. */
.xl-kpi__tip {
  display: none;
  position: absolute;
  bottom: calc(100% + 8px);
  left: 12px;
  z-index: 900;
  width: max-content;
  max-width: 262px;
  padding: 9px 12px;
  border-radius: 10px;
  background: var(--xl-color-on-surface, #1C1720);
  color: var(--xl-color-surface, #fff);
  font-size: var(--xl-type-body-small-size, 12.5px);
  font-weight: 500;
  line-height: 1.5;
  text-align: left;
  white-space: normal;
  pointer-events: none;
}

.xl-kpi__label--explained:hover ~ .xl-kpi__tip,
.xl-kpi__label--explained:focus-visible ~ .xl-kpi__tip,
.xl-kpi--clickable:focus-visible .xl-kpi__tip {
  display: block;
}

.xl-kpi--clickable:hover {
  border-color: var(--xl-color-primary-fixed-dim);
  box-shadow: var(--xl-shadow-2);
}

.xl-kpi--clickable:focus-visible {
  outline: 2px solid var(--xl-color-primary);
  outline-offset: 2px;
}

.xl-kpi__label {
  display: block;
  font-size: var(--xl-type-body-small-size);
  font-weight: 500;
  color: var(--xl-color-outline);
}

.xl-kpi__value {
  display: block;
  margin-top: 4px;
  font-size: 28px;
  font-weight: var(--xl-font-weight-bold);
  letter-spacing: -0.03em;
  /* Stops the row jittering as counts refresh. */
  font-variant-numeric: tabular-nums;
  color: var(--xl-color-primary-text);
}

.xl-kpi__value-sk { display: inline-block; vertical-align: middle; }

.xl-kpi__value--brand { color: var(--xl-color-primary); }
.xl-kpi__value--success { color: var(--xl-color-success, var(--xl-color-primary-text)); }
.xl-kpi__value--warning { color: var(--xl-color-warning); }
.xl-kpi__value--error { color: var(--xl-color-error); }

.xl-kpi__hint {
  display: block;
  margin-top: 4px;
  font-size: var(--xl-type-label-small-size);
  font-weight: 600;
  /* A text token, not the border one. --xl-color-outline-variant is #E0E0E0,
     the same value as --xl-color-border, which put grey hairline text on a
     white card at about 1.3:1 — quiet is the intent, invisible was not. */
  color: var(--xl-color-secondary-text);
}
</style>
